import type { Area } from 'react-easy-crop';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject(new Error('Не удалось загрузить изображение')));
    img.src = src;
  });
}

/** Ограничение выходного размера после кропа (пиксели), с сохранением пропорций. */
export type CroppedImageBlobLimits = {
  maxOutputWidth?: number;
  maxOutputHeight?: number;
};

function outputScaleForLimits(w: number, h: number, limits?: CroppedImageBlobLimits): number {
  if (!limits) return 1;
  let s = 1;
  if (limits.maxOutputWidth != null && limits.maxOutputWidth > 0) {
    s = Math.min(s, limits.maxOutputWidth / w);
  }
  if (limits.maxOutputHeight != null && limits.maxOutputHeight > 0) {
    s = Math.min(s, limits.maxOutputHeight / h);
  }
  return Math.min(1, s);
}

/** Вырезает область в исходных пикселях и отдаёт JPEG-blob. */
export async function getCroppedImageBlob(
  imageSrc: string,
  pixelCrop: Area,
  quality: number,
  mimeType: 'image/jpeg' = 'image/jpeg',
  limits?: CroppedImageBlobLimits
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const w = Math.max(1, Math.round(pixelCrop.width));
  const h = Math.max(1, Math.round(pixelCrop.height));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas недоступен');
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    Math.round(pixelCrop.x),
    Math.round(pixelCrop.y),
    Math.round(pixelCrop.width),
    Math.round(pixelCrop.height),
    0,
    0,
    w,
    h
  );

  const scale = outputScaleForLimits(w, h, limits);
  let outCanvas = canvas;
  if (scale < 1) {
    const outW = Math.max(1, Math.round(w * scale));
    const outH = Math.max(1, Math.round(h * scale));
    const scaled = document.createElement('canvas');
    scaled.width = outW;
    scaled.height = outH;
    const sctx = scaled.getContext('2d');
    if (!sctx) throw new Error('Canvas недоступен');
    sctx.imageSmoothingEnabled = true;
    sctx.imageSmoothingQuality = 'high';
    sctx.drawImage(canvas, 0, 0, w, h, 0, 0, outW, outH);
    outCanvas = scaled;
  }

  return new Promise((resolve, reject) => {
    outCanvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Пустое изображение'));
      },
      mimeType,
      quality
    );
  });
}
